import axios from 'axios'

interface IAuthSessionResponse {
  token: string
  csrf_token: string
}

const authApi = axios.create({
  baseURL: process.env.REACT_APP_MAIN_API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

let accessToken = ''
let csrfToken = ''
let refreshPromise: Promise<IAuthSessionResponse | null> | null = null

const setAuthenticatedSession = (token: string, csrf: string): void => {
  accessToken = token
  csrfToken = csrf
}

const clearAuthenticatedSession = (): void => {
  accessToken = ''
  csrfToken = ''
}

const getAccessToken = (): string => accessToken

const getCsrfToken = async (): Promise<string | null> => {
  const response = await authApi.get('/auth/csrf')
  const token: string | undefined = response.data?.csrf_token

  if (!token) return null

  csrfToken = token
  return csrfToken
}

const refreshAccessToken = async (): Promise<IAuthSessionResponse | null> => {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const csrf: string | null = csrfToken || (await getCsrfToken())
      if (!csrf) return null

      const response = await authApi.post('/auth/refresh', {}, { headers: { 'X-CSRF-Token': csrf } })
      const session: IAuthSessionResponse = response.data

      if (!session.token || !session.csrf_token) return null

      setAuthenticatedSession(session.token, session.csrf_token)
      return session
    } catch {
      clearAuthenticatedSession()
      return null
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

const logoutSession = async (): Promise<void> => {
  try {
    const csrf: string | null = csrfToken || (await getCsrfToken())
    if (csrf) {
      await authApi.post(
        '/auth/logout',
        {},
        {
          headers: { 'X-CSRF-Token': csrf },
        }
      )
    }
  } finally {
    clearAuthenticatedSession()
  }
}

export {
  clearAuthenticatedSession,
  getAccessToken,
  logoutSession,
  refreshAccessToken,
  setAuthenticatedSession,
}
